import React, { useEffect, useRef, useState } from "react";
import Head from "next/head";

const SLIDE_PANEL_ID = "tu-hero-slide-panel";

function shuffle(items) {
	const copy = items.slice();
	for (let i = copy.length - 1; i > 0; i -= 1) {
		const j = Math.floor(Math.random() * (i + 1));
		const swap = copy[i];
		copy[i] = copy[j];
		copy[j] = swap;
	}
	return copy;
}

/** Random image order. The first image is not the one just shown, when there is another. */
function imageOrder(length, avoid) {
	const order = shuffle(Array.from({ length }, (_, index) => index));
	if (length > 1 && avoid != null && order[0] === avoid) {
		const j = 1 + Math.floor(Math.random() * (length - 1));
		const swap = order[0];
		order[0] = order[j];
		order[j] = swap;
	}
	return order;
}

/** Folder 0, then each other folder once, in random order. */
function makeLap(count) {
	const rest = shuffle(Array.from({ length: count - 1 }, (_, index) => index + 1));
	return [0, ...rest];
}

function singleImage(section) {
	return !section || section.banner || section.images.length <= 1;
}

/** Visible titles. Keyed by file name in public/images/new-courel. */
const SLIDE_NAMES = {
	"a10v.svg": { en: "A10V", es: "A10V" },
	"a4v.svg": { en: "A4V", es: "A4V" },
	"v10_single.svg": { en: "V10/V20", es: "V10/V20" },
	"vho-vq-double.svg": { en: "VHO/VQ", es: "VHO/VQ" },
	"v200_1.svg": { en: "V200", es: "V200" },
	"vtm42_1.svg": { en: "VTM42", es: "VTM42" },
	"50v_1.svg": { en: "50V", es: "50V" },
	"th_tie_rod.png": { en: "Tie-rod 3000 cylinders", es: "Cilindros tie-rod 3000" },
	"cw_welded.png": { en: "Welded clevis cylinders", es: "Cilindros con clevis soldado" },
	"welded_cylinder.png": { en: "Welded tube cylinders", es: "Cilindros de tubo soldado" },
	"snow_plow_cylinder.png": { en: "Snow plow cylinders", es: "Cilindros quitanieve" },
	"h-series-101-motor-plate.png": { en: "101 H series", es: "Serie 101 H" },
	"s-series-103-motor-plate.png": { en: "103 S series", es: "Serie 103 S" },
	"2000-series-104-motor.png": { en: "104 2000 series", es: "Serie 104 2000" },
	"2000-series-104y-motor.png": { en: "104Y 2000 series", es: "Serie 104Y 2000" },
	"4000-series-109-motor.png": { en: "109 4000 series", es: "Serie 109 4000" },
	"6000-series-112-motor.png": { en: "112 6000 series", es: "Serie 112 6000" },
	"10000-series-119-motor.png": { en: "119 10000 series", es: "Serie 119 10000" },
	"j-series-129-motor-plate.png": { en: "129 J series", es: "Serie 129 J" },
	"500-series-motor-plate.png": { en: "500 series", es: "Serie 500" },
	"fm1-series-motor-plate.png": { en: "FM1 series", es: "Serie FM1" },
	"fm3-series-motor-plate.png": { en: "FM3 series", es: "Serie FM3" },
	"fm4-series-motor-plate.png": { en: "FM4 series", es: "Serie FM4" },
	"fm50-series-motor.png": { en: "FM50 series", es: "Serie FM50" },
	"fm51-series-motor.png": { en: "FM51 series", es: "Serie FM51" },
	"vickers_d03.svg": { en: "D03 directional", es: "D03 direccional" },
	"vickers_d05.svg": { en: "D05 directional", es: "D05 direccional" },
	"vickers_d07.svg": { en: "D07 directional", es: "D07 direccional" },
	"vickers_d08.svg": { en: "D08 directional", es: "D08 direccional" },
	"rexroth_valve.svg": { en: "D03/D05 directional", es: "D03/D05 direccional" },
	"manual_d03.svg": { en: "Manual D03 directional", es: "Manual D03 direccional" },
	"manual_d05.svg": { en: "Manual D05 directional", es: "Manual D05 direccional" },
	"flow_control_valve.svg": { en: "Flow control", es: "Control de caudal" },
	"pressure_control_valve_1.svg": { en: "Pressure control", es: "Control de presión" },
	"pressure_control_valve_2.svg": { en: "Pressure control", es: "Control de presión" },
	"telescopic_cylinder_1.png": { en: "Telescopic pin and trunnion", es: "Telescópico pasador y muñón" },
	"telescopic_cylinder_trunnion_mount.png": { en: "Telescopic trunnion", es: "Telescópico muñón" },
	"telescopic_cylinder_7_and_12_tons.png": { en: "Telescopic 7 and 12 ton", es: "Telescópico 7 y 12 toneladas" },
	"tu-world2.svg": { en: "TU Industrial World Network", es: "TU Industrial World Network" },
};

function fileName(src) {
	return decodeURIComponent(src.split("/").pop() || "").toLowerCase();
}

function fileLabel(src) {
	const file = decodeURIComponent(src.split("/").pop() || "");
	const stem = file.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ");
	return stem || file;
}

function namedSlide(image, locale) {
	const named = SLIDE_NAMES[fileName(image.src)];
	if (!named) return "";
	return locale === "es" ? named.es : named.en;
}

function slideLabel(image, locale) {
	if (String(image.src || "").includes("/section_13/")) {
		return locale === "es" ? "Cilindros NFPA" : "NFPA cylinders";
	}
	return namedSlide(image, locale) || fileLabel(image.src);
}

export default function HomeHero({ locale = "en", sections = [], priority = true }) {
	const [active, setActive] = useState(0);
	const [picks, setPicks] = useState(() => sections.map(() => 0));
	const [lap, setLap] = useState([0]);
	const [lapPos, setLapPos] = useState(0);
	const paused = useRef(false);
	const activeRef = useRef(0);
	const picksRef = useRef(picks);
	const sectionsRef = useRef(sections);
	const lapRef = useRef([0]);
	const lapPosRef = useRef(0);
	const decksRef = useRef([]);
	const advanceRef = useRef(() => {});
	const slidesLabel = locale === "es" ? "Diapositivas" : "Slides";

	const count = sections.length;
	sectionsRef.current = sections;
	picksRef.current = picks;
	const section = sections[active] || null;
	const image = section ? section.images[picks[active]] || section.images[0] : null;
	const firstSrc = sections[0]?.images[0]?.src || "";

	const rememberPick = (index, imageIndex) => {
		const total = sectionsRef.current.length;
		setPicks((prev) => {
			const copy = prev.length === total ? prev.slice() : sectionsRef.current.map(() => 0);
			copy[index] = imageIndex;
			return copy;
		});
	};

	const drawImage = (index) => {
		const current = sectionsRef.current[index];
		if (singleImage(current)) return 0;
		const length = current.images.length;
		let deck = decksRef.current[index];
		if (!deck || deck.cursor >= deck.order.length) {
			const avoid = deck && deck.order.length ? deck.order[deck.order.length - 1] : undefined;
			deck = { order: imageOrder(length, avoid), cursor: 0 };
			decksRef.current[index] = deck;
		}
		const imageIndex = deck.order[deck.cursor];
		deck.cursor += 1;
		if (deck.cursor >= deck.order.length) {
			decksRef.current[index] = { order: imageOrder(length, imageIndex), cursor: 0 };
		}
		return imageIndex;
	};

	const upcomingPick = (index) => {
		const current = sections[index];
		if (singleImage(current)) return 0;
		const deck = decksRef.current[index];
		if (deck && deck.cursor < deck.order.length) return deck.order[deck.cursor];
		return picks[index] || 0;
	};

	const showFolder = (index, pos, nextLap, consume) => {
		lapRef.current = nextLap;
		lapPosRef.current = pos;
		activeRef.current = index;
		setLap(nextLap);
		setLapPos(pos);
		if (consume && !singleImage(sectionsRef.current[index])) {
			rememberPick(index, drawImage(index));
		} else {
			const deck = decksRef.current[index];
			const shown = picksRef.current[index] || 0;
			if (deck && deck.cursor < deck.order.length && deck.order[deck.cursor] === shown) {
				deck.cursor += 1;
				if (deck.cursor >= deck.order.length) {
					decksRef.current[index] = {
						order: imageOrder(deck.order.length, shown),
						cursor: 0,
					};
				}
			}
		}
		setActive(index);
	};

	const advance = () => {
		const total = sectionsRef.current.length;
		if (total < 2) return;
		let nextLap = lapRef.current.length === total ? lapRef.current : makeLap(total);
		let pos = lapPosRef.current + 1;
		if (pos >= nextLap.length) {
			nextLap = makeLap(total);
			pos = 0;
		}
		const index = nextLap[pos];
		showFolder(index, pos, nextLap, index !== 0);
	};

	const retreat = () => {
		const total = sectionsRef.current.length;
		const nextLap = lapRef.current;
		if (total < 2 || nextLap.length < 2) return;
		const pos = lapPosRef.current === 0 ? nextLap.length - 1 : lapPosRef.current - 1;
		showFolder(nextLap[pos], pos, nextLap, false);
	};

	advanceRef.current = advance;

	useEffect(() => {
		activeRef.current = active;
	}, [active]);

	useEffect(() => {
		if (count < 2) return undefined;
		const decks = sections.map((item) => {
			if (singleImage(item)) return null;
			return { order: imageOrder(item.images.length), cursor: 0 };
		});
		const nextLap = makeLap(count);
		decksRef.current = decks;
		lapRef.current = nextLap;
		lapPosRef.current = 0;
		setLap(nextLap);
		setLapPos(0);
		setPicks(sections.map((item, index) => (decks[index] ? decks[index].order[0] : 0)));
		return undefined;
	}, [count, sections]);

	useEffect(() => {
		if (count < 2) return undefined;
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
		let id;
		const start = () => {
			id = window.setInterval(() => {
				if (paused.current || document.visibilityState === "hidden") return;
				advanceRef.current();
			}, 2500);
		};
		if (document.readyState === "complete") {
			start();
		} else {
			window.addEventListener("load", start, { once: true });
		}
		return () => {
			window.removeEventListener("load", start);
			if (id) window.clearInterval(id);
		};
	}, [count]);

	if (!section || !image) return null;

	const caption = slideLabel(image, locale);

	const choose = (index) => {
		if (count < 2) return;
		if (index === 0) {
			showFolder(0, 0, makeLap(count), false);
			return;
		}
		const nextLap = lapRef.current.length === count ? lapRef.current : makeLap(count);
		const pos = nextLap.indexOf(index);
		showFolder(index, pos >= 0 ? pos : lapPosRef.current, nextLap, true);
	};

	const step = (delta) => {
		if (delta < 0) retreat();
		else advance();
	};

	const folderAt = (delta) => {
		const size = lap.length;
		if (size < 2) return 0;
		return lap[(lapPos + delta + size) % size];
	};

	const onCarouselKeyDown = (event) => {
		if (event.key === "ArrowLeft") {
			event.preventDefault();
			step(-1);
		} else if (event.key === "ArrowRight") {
			event.preventDefault();
			step(1);
		}
	};

	const neighborLabel = (delta) => {
		const index = folderAt(delta);
		const item = sections[index];
		if (!item) return "";
		const pickIndex = delta > 0 ? upcomingPick(index) : picks[index] || 0;
		const picked = item.images[pickIndex] || item.images[0];
		return slideLabel(picked, locale);
	};

	return (
		<>
			{priority && firstSrc ? (
				<Head>
					<link rel="preload" as="image" href={firstSrc} fetchPriority="high" />
				</Head>
			) : null}
			<section
				className="tu-hero"
				aria-roledescription="carousel"
				aria-label={locale === "es" ? "Presentación" : "Hero slideshow"}
				onPointerEnter={() => {
					paused.current = true;
				}}
				onPointerLeave={() => {
					paused.current = false;
				}}
				onKeyDown={onCarouselKeyDown}
			>
				<div
					id={SLIDE_PANEL_ID}
					role="tabpanel"
					aria-roledescription="slide"
					aria-labelledby={`tu-hero-tab-${active}`}
					className="tu-hero__slide is-active"
				>
						<div className="tu-hero__link">
							<span className="tu-hero__plate">
								{/* eslint-disable-next-line @next/next/no-img-element */}
								<img
									key={image.src}
									className="tu-hero__photo"
									alt=""
									src={image.src}
									width={image.width}
									height={image.height}
									decoding="async"
									fetchPriority={priority && active === 0 ? "high" : "auto"}
									loading="eager"
								/>
							</span>
							{caption ? <span className="tu-hero__caption">{caption}</span> : null}
						</div>
					{count > 1 ? (
						<>
							<button
								type="button"
								className="tu-hero__nav tu-hero__nav--prev"
								aria-label={
									locale === "es"
										? `Diapositiva anterior: ${neighborLabel(-1)}`
										: `Previous slide: ${neighborLabel(-1)}`
								}
								onClick={() => step(-1)}
							>
								<svg className="tu-hero__chevron" viewBox="0 0 24 24" aria-hidden="true">
									<path d="M14.5 5.5 8 12l6.5 6.5" />
								</svg>
							</button>
							<button
								type="button"
								className="tu-hero__nav tu-hero__nav--next"
								aria-label={
									locale === "es"
										? `Diapositiva siguiente: ${neighborLabel(1)}`
										: `Next slide: ${neighborLabel(1)}`
								}
								onClick={() => step(1)}
							>
								<svg className="tu-hero__chevron" viewBox="0 0 24 24" aria-hidden="true">
									<path d="M9.5 5.5 16 12l-6.5 6.5" />
								</svg>
							</button>
						</>
					) : null}
				</div>
				<div className="tu-hero__dots" role="tablist" aria-label={slidesLabel}>
					{sections.map((item, index) => {
						const pickIndex = index === active ? picks[index] || 0 : upcomingPick(index);
						const picked = item.images[pickIndex] || item.images[0];
						const label = slideLabel(picked, locale);
						return (
							<button
								key={item.id}
								type="button"
								role="tab"
								id={`tu-hero-tab-${index}`}
								className={`tu-hero__dot${index === active ? " is-active" : ""}`}
								aria-controls={SLIDE_PANEL_ID}
								aria-selected={index === active}
								tabIndex={index === active ? 0 : -1}
								aria-label={
									locale === "es"
										? `Diapositiva ${index + 1} de ${count}: ${label}`
										: `Slide ${index + 1} of ${count}: ${label}`
								}
								onClick={() => choose(index)}
							/>
						);
					})}
				</div>
			</section>
		</>
	);
}
